import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/PageTitle/PageTitle";
import Table from "../../components/Table/Table";
import { TableSearch } from "../../components/Table/TableActions";
import Badge from "../../components/Badge/Badge";
import Modal from "../../components/Modal";
import Button from "../../components/Button/Button";
import { Icon } from "@iconify/react";
import { useState, useEffect } from "react";
import styles from "./rewards.module.css";
import { httprequest } from "../../data/api";
import { toast, ToastContainer } from "react-toastify";

const WITHDRAWALS_ENDPOINT = "/admin/v1/withdrawals";

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }

  return d.toLocaleDateString(navigator.language, { day: "2-digit", month: "short", year: "numeric" });
};

const formatAmount = (currency, amount) => {
  if (amount == null) {
    return "-";
  }
  const amt = String(amount);
  if (currency === "NGN") {
    return `₦${amt}`;
  }
  if (currency === "USD") {
    return `$${amt}`;
  }

  return `${currency || ""} ${amt}`.trim();
};

const Withdrawals = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [approveModal, setApproveModal] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [withdrawalDetail, setWithdrawalDetail] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchWithdrawals = () => {
    setIsLoading(true);
    httprequest
      .get(WITHDRAWALS_ENDPOINT)
      .then((res) => {
        const payload = res?.data?.data;
        const list = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
        setWithdrawals(list);
        setSearchData(list);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.message || "Failed to load withdrawals");
      });
  };

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const handleSearch = (e) => {
    const { value } = e.target;
    const q = (value || "").toLowerCase();
    const result = withdrawals.filter((row) => {
      const name = `${row?.user_firstname || row?.user?.firstname || ""} ${row?.user_lastname || row?.user?.lastname || ""}`.trim();
      const acctName = row?.bank_details?.account_name || row?.account_name || "";
      const currency = row?.currency || "";

      return name.toLowerCase().includes(q) ||
        String(acctName).toLowerCase().includes(q) ||
        String(currency).toLowerCase().includes(q) ||
        String(row?.user_email || row?.user?.email || "").toLowerCase().includes(q);
    });
    setSearchData(result);
  };

  const fetchWithdrawalById = (requestId) => {
    if (!requestId) {
      return;
    }
    setIsDetailLoading(true);
    setWithdrawalDetail(null);

    httprequest
      .get(`${WITHDRAWALS_ENDPOINT}/${requestId}`)
      .then((res) => {
        setIsDetailLoading(false);
        setWithdrawalDetail(res?.data?.data || res?.data || null);
      })
      .catch((err) => {
        setIsDetailLoading(false);
        toast.error(err?.response?.data?.message || "Failed to load withdrawal details");
      });
  };

  const handleApproveModal = (row) => {
    setCurrentRow(row);
    setApproveModal(true);
    fetchWithdrawalById(row?.id);
  };

  const closeModal = () => {
    if (isProcessing) {
      return;
    }
    setApproveModal(false);
    setCurrentRow(null);
    setWithdrawalDetail(null);
  };

  const processWithdrawal = async (action) => {
    const requestId = withdrawalDetail?.id || currentRow?.id;
    if (!requestId) {
      return;
    }

    setIsProcessing(true);
    try {
      await httprequest.patch(`${WITHDRAWALS_ENDPOINT}/${requestId}/process`, { action });

      toast.success(`Withdrawal ${action === "approve" ? "approved" : "rejected"} successfully`);
      closeModal();
      fetchWithdrawals();
    } catch (err) {
      toast.error(err?.response?.data?.message || `Failed to ${action} withdrawal`);
    } finally {
      setIsProcessing(false);
    }
  };

  const makePayment = async () => {
    const requestId = withdrawalDetail?.id || currentRow?.id;
    if (!requestId) return;

    const detail = withdrawalDetail || currentRow;
    const name = `${detail?.user_firstname || detail?.user?.firstname || ""} ${detail?.user_lastname || detail?.user?.lastname || ""}`.trim();
    const account_number = detail?.bank_details?.account_number || detail?.account_number || "";
    const bank_code = detail?.bank_details?.bank_code || detail?.bank_code || "";
    const amount = detail?.amount || 0;
    const user_id = detail?.user_id || detail?.user?.id || "";

    setIsProcessing(true);
    try {
      await httprequest.post("/admin/v1/withdrawals/transfer", [{
        name,
        account_number,
        bank_code,
        amount,
        reason: "Withdrawal for completed task",
        withdrawal_id: requestId,
        request_id: requestId,
        user_id
      }]);
      toast.success("Payment made successfully");
      closeModal();
      fetchWithdrawals();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to make payment");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopy = (text) => {
    if (text && text !== "-") {
      navigator.clipboard.writeText(text);
      toast.success("Account number copied");
    }
  };

  const columns = [
    {
      name: "Recipient Name",
      selector: (row) => {
        const name = `${row?.user_firstname || row?.user?.firstname || ""} ${row?.user_lastname || row?.user?.lastname || ""}`.trim();

        return name || "-";
      },
      sortable: true
    },
    {
      name: "Account Name",
      selector: (row) => row?.bank_details?.account_name || row?.account_name || "-",
      sortable: true
    },
    {
      name: "Currency",
      selector: (row) => row?.currency,
      sortable: true
    },
    {
      name: "Points",
      selector: (row) => row?.points_amount ?? "-",
      sortable: true
    },
    {
      name: "Amount",
      selector: (row) => formatAmount(row?.currency, row?.amount),
      sortable: true
    },
    {
      name: "Created",
      selector: (row) => formatDate(row?.created_at),
      sortable: true
    },
    {
      name: "Status",
      sortable: true,
      cell: (row) => <Badge status={row?.status?.toLowerCase()} />
    },
    {
      name: "",
      center: true,
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          {row?.status?.toLowerCase() === "pending" && (
            <button
              className="table-action-icon"
              onClick={() => handleApproveModal(row)}
              title="View/Approve"
            >
              <Icon icon="fluent:eye-16-regular" />
            </button>
          )}
          {row?.status?.toLowerCase() === "approved" && (
            <button
              className="table-action-icon"
              onClick={() => handleApproveModal(row)}
              title="Payout"
            >
              <Icon icon="fluent:wallet-16-regular" />
            </button>
          )}
        </div>
      )
    }
  ];

  const detail = withdrawalDetail || currentRow;
  const userName =
    `${detail?.user_firstname || detail?.user?.firstname || ""} ${detail?.user_lastname || detail?.user?.lastname || ""}`.trim() ||
    "-";
  const userEmail = detail?.user_email || detail?.user?.email || "-";
  const bankName = detail?.bank_details?.bank_name || detail?.bank_name || "-";
  const accountName = detail?.bank_details?.account_name || detail?.account_name || "-";
  const accountNumber = detail?.bank_details?.account_number || detail?.account_number || "-";
  const amount = formatAmount(detail?.currency, detail?.amount);
  const points = detail?.coins_requested ?? "-";
  const createdAt = formatDate(detail?.created_at);
  const status = detail?.status || "-";

  return (
    <Layout>
      <PageTitle
        title="Withdrawals"
        subtitle="Approve all withdrawal request."
      />
      <Table
        data={searchData}
        columns={columns}
        subHeader={true}
        pagination
        progressPending={isLoading}
        subHeaderComponent={
          <TableSearch
            placeholder="Search here..."
            searchTableFunc={handleSearch}
          />
        }
      />

      {/* Approve Withdrawal Modal */}
      <Modal
        title={detail?.status?.toLowerCase() === "approved" ? "Payout Withdrawal" : "Approve Withdrawal"}
        subtitle={detail?.status?.toLowerCase() === "approved" ? "Make payment via Paystack" : "Approve withdrawals made"}
        modalContent={
          <div className={styles.withdrawalModalContent}>
            {isDetailLoading ? (
              <div style={{ padding: 12 }}>Loading withdrawal details...</div>
            ) : (
              <>
                <div className={styles.withdrawalRow}>
                  <span className={styles.withdrawalLabel}>Status</span>
                  <span className={styles.withdrawalValue}>{status}</span>
                </div>
                <div className={styles.withdrawalRow}>
                  <span className={styles.withdrawalLabel}>Recipient Name</span>
                  <span className={styles.withdrawalValue}>{userName}</span>
                </div>
                <div className={styles.withdrawalRow}>
                  <span className={styles.withdrawalLabel}>Email</span>
                  <span className={styles.withdrawalValue}>{userEmail}</span>
                </div>
                <div className={styles.withdrawalRow}>
                  <span className={styles.withdrawalLabel}>Points Requested</span>
                  <span className={styles.withdrawalValue}>{points}</span>
                </div>
                <div className={styles.withdrawalRow}>
                  <span className={styles.withdrawalLabel}>Amount</span>
                  <span className={styles.withdrawalValue}>{amount}</span>
                </div>
                <div className={styles.withdrawalRow}>
                  <span className={styles.withdrawalLabel}>Bank</span>
                  <span className={styles.withdrawalValue}>{bankName}</span>
                </div>
                <div className={styles.withdrawalRow}>
                  <span className={styles.withdrawalLabel}>Account Name</span>
                  <span className={styles.withdrawalValue}>{accountName}</span>
                </div>
                <div className={styles.withdrawalRow}>
                  <span className={styles.withdrawalLabel}>Account Number</span>
                  <span className={styles.withdrawalValue} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {accountNumber}
                    {accountNumber !== "-" && (
                      <Icon
                        icon="fluent:copy-16-regular"
                        style={{ cursor: "pointer", color: "#666" }}
                        onClick={() => handleCopy(accountNumber)}
                        title="Copy account number"
                      />
                    )}
                  </span>
                </div>
                <div className={styles.withdrawalRow}>
                  <span className={styles.withdrawalLabel}>Created</span>
                  <span className={styles.withdrawalValue}>{createdAt}</span>
                </div>
              </>
            )}
          </div>
        }
        modalFooter={
          <div className="flex gap-10">
            {detail?.status?.toLowerCase() === "pending" ? (
              <>
                <Button
                  variant="dangeroutline"
                  buttonSize="full"
                  color="btndanger"
                  buttonName={isProcessing ? "Processing..." : "Reject Withdrawal"}
                  onClick={() => processWithdrawal("reject")}
                  disabled={isDetailLoading || isProcessing}
                />
                <Button
                  variant="primary"
                  buttonSize="full"
                  color="btndefault"
                  buttonName={isProcessing ? "Processing..." : "Approve Withdrawal"}
                  onClick={() => processWithdrawal("approve")}
                  disabled={isDetailLoading || isProcessing}
                />
              </>
            ) : detail?.status?.toLowerCase() === "approved" ? (
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName={isProcessing ? "Processing..." : "Make Payment"}
                onClick={makePayment}
                disabled={isDetailLoading || isProcessing}
              />
            ) : null}
          </div>
        }
        isopen={approveModal}
        closeModal={closeModal}
      />
      <ToastContainer />
    </Layout>
  );
};

export default Withdrawals;
